import React, { memo, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, Heading, Text } from '@chakra-ui/core'
import keys from 'constants/keys'
import { BackArrow, NavTitleItem } from 'components/Sidebar/SidebarStyles'
import { Loader } from 'components/CommonComponents/Loader'
import LocaleText from 'components/LocaleText'
import { PostWrapper } from 'components/Post'
import { CardWrap, CustomInfiniteScroll } from 'components/helperStyles'
import Search from 'components/CommonComponents/Search/Search'
import { DEVICE_TYPES } from 'constants/UI'
import ChatsPreviewsContainer from 'components/ChatsPreviews'
import { DraggableTasks } from 'components/NewsFeed/DraggableTasks'
import { SearchFilter } from 'components/NewsFeed/SearchFilter'
import { SearchModal } from 'components/CommonComponents/Search/SearchModal'
import { RootState } from 'constants/interfaces'
import { isRtlSelector } from 'redux/selectors/general'
import { setSelectedPost } from 'redux/actions/feed'
import useLocaleText from 'components/useLocaleText'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { CustomSkeleton } from 'components/Elements'
import colors from 'constants/colors'
import { useSetup } from 'contexts/SetupContext'
import { isSetupAllowedForUser, STEPS } from 'utils/setupStorage'

interface NewsFeedViewProps {
  search: string
  sortedPostsIds: string[] | null
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  openSearchModalHandler: () => void
  fetchPosts: () => Promise<void>
  isOpenSearchModal: boolean
  onCloseSearchModal: () => void
}

export const NewsFeedView = memo(
  ({
    search,
    sortedPostsIds,
    onSearchChange,
    openSearchModalHandler,
    fetchPosts,
    isOpenSearchModal,
    onCloseSearchModal,
  }: NewsFeedViewProps) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { windowWidth } = useWindowDimensions()

    const isRtl = useSelector(isRtlSelector)
    const fetching = useSelector((state: RootState) => state.feed.fetching)
    const posts = useSelector((state: RootState) => state.feed.posts)
    const uid = useSelector((state: RootState) => state.auth.uid)
    const hasMorePosts = useSelector((state: RootState) => state.feed.hasMorePosts)
    const searchPosts = useSelector((state: RootState) => state.feed.searchPosts)
    const isSearchFetching = useSelector((state: RootState) => state.feed.isSearchFetching)
    const isAllPostCollapsed = useSelector((state: RootState) => state.feed.isAllPostCollapsed)
    const selectedPostID = useSelector((state: RootState) => state.feed.selectedPostID)

    const isSearchPage = useMemo(() => location.pathname === keys.ROUTE_NAMES.NEWS_FEED_SEARCH, [location.pathname])
    const searchPlaceholder = useLocaleText('search_feed_web_placeholder')
    const offset = 0

    const selectedPost = useMemo(() => posts.find((post) => post.post_id === selectedPostID), [posts, selectedPostID])
    const hasPosts = posts.length > 0
    const hasSearchResults = searchPosts.length > 0
    const showSearchResults = isSearchPage && !isSearchFetching && hasSearchResults
    const showNoSearchResults = isSearchPage && !isSearchFetching && !hasSearchResults
    const showNoPosts = !hasPosts && !fetching

    const { isStepCompleted } = useSetup()
    const isCurrentStepCompleted = isStepCompleted(STEPS.FEED)
    const blockContent = !isCurrentStepCompleted && isSetupAllowedForUser(uid!)

    const handleOpenSelectedPost = (postId: string) => {
      if (!posts || selectedPostID === postId) return
      const currentPost = posts.find((post) => post.post_id === postId)
      if (!currentPost) return
      dispatch(setSelectedPost(currentPost?.post_id))
    }

    useEffect(() => {
      if (!isCurrentStepCompleted && location.pathname === keys.ROUTE_NAMES.NEWS_FEED) {
        setTimeout(() => {
          const event = new CustomEvent('openSetupModal', {
            detail: { stepId: STEPS.FEED, stepTitle: 'Feed' },
          })
          window.dispatchEvent(event)
        }, 500)
      }
    }, [isCurrentStepCompleted, location.pathname])

    return (
      <Flex flex="1" flexDir="column" maxW={isAllPostCollapsed ? '100%' : '700px'} w="100%" mx="auto" px={4}>
        {isSearchPage && (
          <Search
            onClick={openSearchModalHandler}
            value={search}
            onChange={onSearchChange}
            {...(!DEVICE_TYPES.DESKTOP(windowWidth)
              ? {
                  order: -1,
                  width: '100%',
                  _focus: { width: '200px', transition: 'all ease .3s' },
                }
              : { maxW: '600px' })}
            placeholder={searchPlaceholder}
          />
        )}
        <ChatsPreviewsContainer />
        {isSearchPage && (
          <Box mx="50px" mb="10px" position="relative">
            <Flex alignItems="center" height="36px" mb="22px">
              <NavTitleItem
                onClick={() => navigate(keys.ROUTE_NAMES.NEWS_FEED)}
                position="absolute"
                top="0"
                {...(isRtl ? { right: '-70px' } : { left: '-70px' })}
              >
                <BackArrow />
              </NavTitleItem>
              <Heading size="md">
                <LocaleText text="search_feed_result_title" /> {search && <>&ldquo;{search}&rdquo;</>}
              </Heading>
            </Flex>
            <SearchFilter />
          </Box>
        )}

        {!isSearchPage && isAllPostCollapsed !== null && (
          <Flex
            flexDir="row"
            background={isAllPostCollapsed ? 'white' : 'transparent'}
            borderRadius={isAllPostCollapsed ? '12px' : 'none'}
            w="100%"
            pos="relative"
            h={isAllPostCollapsed ? '76vh' : '100%'}
            minW={isAllPostCollapsed ? 'unset' : '600px'}
          >
            <Box pos="fixed" top="325px" zIndex={1000} display={blockContent ? 'none' : 'block'}>
              <DraggableTasks />
            </Box>

            <Box
              w={isAllPostCollapsed ? '40%' : 'inherit'}
              minW={isAllPostCollapsed ? 'unset' : '600px'}
              background="transparent"
              margin={isAllPostCollapsed ? '5px' : 0}
              height={isAllPostCollapsed ? '75vh' : '100%'}
              overflowY={isAllPostCollapsed ? 'scroll' : 'hidden'}
            >
              {showNoPosts ? (
                <Text color="#b7b8b8" fontSize="26px" fontWeight="700" mt="25%" textAlign="center">
                  <LocaleText text="no_posts_found" />
                </Text>
              ) : (
                <CustomInfiniteScroll
                  pageStart={offset}
                  loadMore={fetchPosts}
                  hasMore={isSetupAllowedForUser(uid!) ? hasMorePosts && isCurrentStepCompleted : hasMorePosts}
                  threshold={500}
                  loader={<Loader key="unique-loader-key" />}
                  isRtl={isRtl}
                  isAllPostCollapsed={isAllPostCollapsed}
                  useWindow={isAllPostCollapsed ? false : true}
                >
                  <>
                    <Box display={blockContent ? 'block' : 'none'}>
                      {Array.from({ length: 10 }).map((_, index) => (
                        <CardWrapSkeleton key={index} isAllpostCollapsed={isAllPostCollapsed} doublePost={false} />
                      ))}
                    </Box>

                    <Box display={blockContent ? 'none' : 'block'}>
                      {posts
                        .filter((post) => (sortedPostsIds ? sortedPostsIds.includes(post.post_id) : true))
                        .map((post, index) => (
                          <PostWrapper
                            key={post.post_id}
                            index={index}
                            {...post}
                            handleOpenSelectedPost={handleOpenSelectedPost}
                            isAllPostCollapsed={isAllPostCollapsed}
                            posts={posts}
                            isDoublePost={false}
                            selectedPostID={selectedPostID === post.post_id ? selectedPostID : undefined}
                          />
                        ))}
                    </Box>
                  </>
                </CustomInfiniteScroll>
              )}
            </Box>
            {isAllPostCollapsed && (
              <Box
                border="1px solid #efefef"
                borderRadius="12px"
                w="59%"
                ml={isRtl ? '6px' : '0'}
                mr={isRtl ? '0' : '6px'}
                h="75vh"
                overflowY="auto"
                my="6px"
              >
                {blockContent ? (
                  <CardWrapSkeleton isAllpostCollapsed={isAllPostCollapsed} doublePost={true} />
                ) : (
                  <>
                    {selectedPost ? (
                      <PostWrapper
                        {...selectedPost}
                        handleOpenSelectedPost={handleOpenSelectedPost}
                        isDoublePost={isAllPostCollapsed ? true : false}
                        isAllPostCollapsed={isAllPostCollapsed}
                        selectedPostID={selectedPostID === selectedPost.post_id ? selectedPostID : undefined}
                        posts={posts}
                      />
                    ) : (
                      <Text color="#b7b8b8" fontSize="26px" fontWeight="700" mt="17%" textAlign="center">
                        <LocaleText text="no_conversation_selected" />
                      </Text>
                    )}
                  </>
                )}
              </Box>
            )}
          </Flex>
        )}

        {isSearchFetching && <Loader />}
        {showSearchResults && (
          <Box
            mx="60px"
            width={{
              lg: '48.5vw',
              base: '100%',
              md: '100vw',
            }}
          >
            {searchPosts.map((post) => (
              <PostWrapper key={post.post_id} {...post} isAllPostCollapsed={false} posts={searchPosts} />
            ))}
          </Box>
        )}

        {showNoSearchResults && (
          <Flex justifyContent="center" mt="25px" maxWidth="600px">
            <Heading size="md">
              <LocaleText text="search_feed_no_results" />
            </Heading>
          </Flex>
        )}
        <SearchModal isOpen={isOpenSearchModal} onClose={onCloseSearchModal} />
      </Flex>
    )
  }
)

interface CardWrapSkeletonProps {
  isAllpostCollapsed?: boolean
  doublePost?: boolean
}

export const CardWrapSkeleton: React.FC<CardWrapSkeletonProps> = ({ isAllpostCollapsed, doublePost }) => {
  const height = isAllpostCollapsed && !doublePost ? '130px' : '250px'

  return (
    <CardWrap
      isAllpostCollapsed={isAllpostCollapsed}
      doublePost={doublePost}
      padding="5px !important"
      marginBottom="0px !important"
    >
      <CustomSkeleton height={height} width="100%" colorStart={colors.greyLight} colorEnd="#d2d5d8" />
    </CardWrap>
  )
}
